<template>
    <app-layout>
        <common-page-plain>
            <common-content-spacer height="medium" :isCenter="true">
                <common-page-header
                    size="page-plain-header"
                    :heading="$t('pages.administrator.manage_license_status.heading')"
                ></common-page-header>

                <div class="license-table">
                    <div class="license-table__header">
                        <div class="license-table__header__item">
                            {{ $t('pages.administrator.manage_license_status.table.user') }}
                        </div>
                        <div class="license-table__header__item">
                            {{ $t('pages.administrator.manage_license_status.table.npi_number') }}
                        </div>
                        <div class="license-table__header__item">
                            {{ $t('pages.administrator.manage_license_status.table.license_file') }}
                        </div>
                        <div class="license-table__header__item">
                            {{ $t('pages.administrator.manage_license_status.table.uploaded_date') }}
                        </div>
                        <div class="license-table__header__item">
                            {{ $t('pages.administrator.manage_license_status.table.actions') }}
                        </div>
                    </div>

                    <div v-if="isLoading" class="license-table__loading">
                        <common-loader :width="56" :isProcessing="isLoading"></common-loader>
                    </div>

                    <div v-else-if="licenseRequests.length === 0" class="license-table__empty">
                        <p>{{ $t('pages.administrator.manage_license_status.table.no_requests') }}</p>
                    </div>

                    <div v-else class="license-table__content">
                        <div v-for="(request, index) in licenseRequests" :key="index" class="license-table__row">
                            <div class="license-table__row__item license-table__row__item--user">
                                <common-user-avatar :user="request.user" size="user-menu"></common-user-avatar>
                                <div class="license-table__row__item--user--info">
                                    <p class="license-table__row__item--user--name">{{ request.user.name }}</p>
                                </div>
                            </div>
                            <div class="license-table__row__item">
                                <p>{{ request.npiNumber || '-' }}</p>
                            </div>
                            <div class="license-table__row__item">
                                <div v-if="request.licenseFileName" class="license-file-cell">
                                    <common-images :images="[getImageObject(request)]" :showMoreOption="false"></common-images>
                                </div>
                                <span v-else>-</span>
                            </div>
                            <div class="license-table__row__item">
                                <p>{{ formatDate(request.uploadedAt) }}</p>
                            </div>
                            <div class="license-table__row__item">
                                <div v-if="request.licenseFileName" class="license-actions">
                                    <common-buttons>
                                        <common-button
                                            :label="$t('pages.administrator.manage_license_status.table.approve')"
                                            :isProcessing="request.isApproving"
                                            type="primary"
                                            size="small"
                                            @click="approveLicense(request)"
                                        ></common-button>
                                        <common-button
                                            :label="$t('pages.administrator.manage_license_status.table.reject')"
                                            :isProcessing="request.isRejecting"
                                            type="secondary"
                                            size="small"
                                            @click="rejectLicense(request)"
                                        ></common-button>
                                    </common-buttons>
                                </div>
                                <span v-else>-</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="totalPages > 1" class="license-pagination">
                    <common-button
                        :isDisabled="currentPage === 1"
                        :label="$t('common.microcopy.previous')"
                        type="secondary"
                        size="small"
                        @click="changePage(currentPage - 1)"
                    ></common-button>
                    <span class="license-pagination__info">
                        {{
                            $t('pages.administrator.manage_license_status.pagination.info', {
                                current: currentPage,
                                total: totalPages,
                            })
                        }}
                    </span>
                    <common-button
                        :isDisabled="currentPage === totalPages"
                        :label="$t('common.microcopy.next')"
                        type="secondary"
                        size="small"
                        @click="changePage(currentPage + 1)"
                    ></common-button>
                </div>

                <common-modal v-if="showLicenseModal" :defaultCloseButtonEnabled="true" @close="closeLicenseModal">
                    <div class="license-modal">
                        <h3>{{ $t('pages.administrator.manage_license_status.modal.title') }}</h3>
                        <div class="license-modal__content">
                            <div class="view-license__documents">
                                <img
                                    v-if="selectedLicenseFile && isImageFile(selectedLicenseFile)"
                                    :src="selectedLicenseFile"
                                    alt="License Document"
                                    style="max-width: 100%; max-height: 550px; object-fit: contain"
                                />
                                <embed v-else-if="selectedLicenseFile" :src="selectedLicenseFile" width="100%" height="550px" />
                            </div>

                            <common-buttons>
                                <common-button
                                    :isSubmittable="false"
                                    :label="$t('pages.referrals.notes.view.buttons.open_in_new_tab')"
                                    type="secondary"
                                    size="large"
                                    @click="openInNewTab"
                                ></common-button>
                                <common-button
                                    :isSubmittable="false"
                                    :label="$t('pages.referrals.notes.view.buttons.download_report')"
                                    type="primary"
                                    size="large"
                                    @click="downloadLicense"
                                ></common-button>
                            </common-buttons>
                        </div>
                    </div>
                </common-modal>

                <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
            </common-content-spacer>
        </common-page-plain>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import FileMixin from 'mixins/FileMixin'
    import AdministratorAPI from 'api/AdministratorAPI'
    import dayjs from 'dayjs'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, FileMixin],

        components: {
            AppLayout,
        },

        data() {
            return {
                licenseRequests: [],
                isLoading: false,
                currentPage: 1,
                totalPages: 1,
                notificationMessage: '',
                showLicenseModal: false,
                selectedLicenseFile: null,
                currentFileName: null,
                currentContentType: null,
            }
        },

        methods: {
            loadLicenseRequests() {
                this.isLoading = true
                AdministratorAPI.getLicenseRequests({
                    page: this.currentPage,
                    limit: 20,
                })
                    .then((response) => {
                        this.licenseRequests = response.items || []
                        this.totalPages = Math.ceil((response.total || 0) / 20)
                    })
                    .catch((error) => {
                        console.error('Error loading license requests:', error)
                        this.notificationMessage = this.$t('pages.administrator.manage_license_status.notifications.load_failed')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            getImageObject(request) {
                const apiUrl = process.env.VUE_APP_API_URL
                return {
                    url: `${apiUrl}/admin/license-requests/file/${request.licenseFileName}`,
                    note: request.licenseFileName,
                    extension: request.licenseFileName.split('.').pop().toLowerCase(),
                }
            },

            changePage(page) {
                this.currentPage = page
                this.loadLicenseRequests()
            },

            formatDate(date) {
                return dayjs(date).format('MMM DD, YYYY')
            },

            viewLicenseFile(request) {
                this.showLicenseModal = true
                AdministratorAPI.getLicenseFile(request.licenseFileName)
                    .then((blob) => {
                        if (!blob || !(blob instanceof Blob)) {
                            throw new Error('Invalid response - expected blob')
                        }

                        this.selectedLicenseFile = window.URL.createObjectURL(blob)
                        this.currentFileName = request.licenseFileName
                        this.currentContentType = blob.type
                    })
                    .catch((error) => {
                        console.error('Error loading license file:', error)

                        if (error.message && error.message.includes('404')) {
                            this.notificationMessage = this.$t(
                                'pages.administrator.manage_license_status.notifications.file_not_found',
                            )
                        } else {
                            this.notificationMessage = this.$t(
                                'pages.administrator.manage_license_status.notifications.load_file_failed',
                            )
                        }

                        this.$refs.commonSuccessNotificationChild.showNotification()
                        this.showLicenseModal = false
                    })
            },

            approveLicense(request) {
                const requestIndex = this.licenseRequests.findIndex((r) => r.user.id === request.user.id)
                if (requestIndex !== -1) {
                    this.$set(this.licenseRequests[requestIndex], 'isApproving', true)
                }

                AdministratorAPI.approveLicense(request.user.id)
                    .then(() => {
                        this.notificationMessage = this.$t(
                            'pages.administrator.manage_license_status.notifications.approve_success',
                        )
                        this.$refs.commonSuccessNotificationChild.showNotification()

                        this.loadLicenseRequests()
                    })
                    .catch((error) => {
                        console.error('Error approving license:', error)
                        this.notificationMessage = this.$t(
                            'pages.administrator.manage_license_status.notifications.approve_failed',
                        )
                        this.$refs.commonSuccessNotificationChild.showNotification()

                        if (requestIndex !== -1) {
                            this.$set(this.licenseRequests[requestIndex], 'isApproving', false)
                        }
                    })
            },

            rejectLicense(request) {
                const requestIndex = this.licenseRequests.findIndex((r) => r.user.id === request.user.id)
                if (requestIndex !== -1) {
                    this.$set(this.licenseRequests[requestIndex], 'isRejecting', true)
                }

                AdministratorAPI.rejectLicense(request.user.id)
                    .then(() => {
                        this.notificationMessage = this.$t(
                            'pages.administrator.manage_license_status.notifications.reject_success',
                        )
                        this.$refs.commonSuccessNotificationChild.showNotification()

                        this.loadLicenseRequests()
                    })
                    .catch((error) => {
                        console.error('Error rejecting license:', error)
                        this.notificationMessage = this.$t(
                            'pages.administrator.manage_license_status.notifications.reject_failed',
                        )
                        this.$refs.commonSuccessNotificationChild.showNotification()

                        if (requestIndex !== -1) {
                            this.$set(this.licenseRequests[requestIndex], 'isRejecting', false)
                        }
                    })
            },

            closeLicenseModal() {
                this.showLicenseModal = false
                if (this.selectedLicenseFile && this.selectedLicenseFile.startsWith('blob:')) {
                    window.URL.revokeObjectURL(this.selectedLicenseFile)
                }
                this.selectedLicenseFile = null
                this.currentFileName = null
                this.currentContentType = null
            },

            isImageFile(url) {
                if (!url) return false

                if (url.startsWith('blob:') && this.currentContentType) {
                    return this.currentContentType.startsWith('image/')
                }

                const extension = this.currentFileName ? this.currentFileName.split('.').pop().toLowerCase() : null
                return extension && extension !== 'pdf'
            },

            openInNewTab() {
                if (this.selectedLicenseFile) {
                    window.open(this.selectedLicenseFile, '_blank')
                }
            },

            downloadLicense() {
                if (this.selectedLicenseFile && this.currentFileName) {
                    this.downloadFile(this.selectedLicenseFile, this.currentFileName)
                }
            },
        },

        mounted() {
            this.loadLicenseRequests()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .license-table {
        width: 100%;
        border: 1px solid $color--border-base;
        border-radius: $border-radius--base;
        overflow: hidden;

        &__header {
            display: grid;
            grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
            background-color: $color--bg-dropdown;
            padding: 1rem;
            font-weight: bold;
            border-bottom: 1px solid $color--border-base;

            &__item {
                padding: 0.5rem;
            }
        }

        &__loading,
        &__empty {
            padding: 3rem;
            text-align: center;
            color: $color--txt-minor;
        }

        &__loading {
            .loading-spinner {
                display: flex;
                justify-content: center;
                align-items: center;
            }
        }

        &__content {
            .license-table__row {
                display: grid;
                grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
                padding: 1rem;
                border-bottom: 1px solid $color--border-base;
                align-items: center;

                &:hover {
                    background-color: $color--bg-dropdown;
                }

                &__item {
                    padding: 0.5rem;

                    &--user {
                        display: flex;
                        align-items: center;
                        gap: 1rem;

                        &--info {
                            .license-table__row__item--user--name {
                                font-weight: bold;
                                margin: 0;
                            }

                            .license-table__row__item--user--email {
                                color: $color--txt-minor;
                                font-size: 0.9rem;
                                margin: 0;
                            }
                        }
                    }
                }
            }
        }
    }

    .license-pagination {
        margin-top: 2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;

        &__info {
            color: $color--txt-minor;
        }
    }

    .license-file-cell {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        .license-file-name {
            font-size: 0.9rem;
            color: $color--txt-base;
            word-break: break-all;
        }

        .images {
            .images--list {
                .images__image {
                    .images__thumbnail {
                        cursor: pointer;
                        transition: opacity 0.2s ease;

                        &:hover {
                            opacity: 0.8;
                        }
                    }
                }
            }
        }
    }

    .license-actions {
        display: flex;
        justify-content: center;
        align-items: center;

        .common-buttons {
            display: flex;
            gap: 0.5rem;
            flex-direction: column;

            .common-button {
                min-width: 80px;
                font-size: 0.8rem;
                padding: 0.4rem 0.8rem;
            }
        }
    }

    .view-license {
        &__documents {
            height: 60vh;
            text-align: center;
        }
    }

    .license-modal {
        padding: 2rem;

        h3 {
            margin-bottom: 1rem;
        }

        &__content {
            max-height: 600px;
            overflow-y: auto;

            img {
                max-width: 100%;
                height: auto;
            }
        }
    }
</style>
